import React from "react";
import { useQuery } from "@tanstack/react-query";
import { INSTAGRAM_IMAGES } from "../../constants/QueryKeys";
import config from "../../config/app";
import DesignContextUtils from "../../utils/DesignContextUtils";
import PlaceholderBackground from "../utils/PlaceholderBackground.jsx";
import { DESIGN_CONTEXT } from "../../constants/DesignContextConstants";

const placeholderIcon = (
  <svg
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    x="0px"
    y="0px"
    viewBox="0 0 24 24"
    enableBackground="new 0 0 24 24"
    style={{ width: "100%", height: "100%" }}
  >
    <g fill="#fff">
      <path d="M23,6h-2v2h1v12H6v-1H4v2c0,0.5,0.5,1,1,1h18c0.5,0,1-0.5,1-1V7C24,6.5,23.5,6,23,6z" />
      <circle cx="5" cy="7" r="1.4" />
      <path
        d="M19,2H1C0.5,2,0,2.5,0,3v14c0,0.5,0.5,1,1,1h18c0.5,0,1-0.5,1-1V3C20,2.5,19.5,2,19,2z M18,4v6.3l-3.6-2.7
                                    c0,0-0.8-0.8-1.5-0.8c-0.8,0-1.5,0.8-1.5,0.8s-2.1,2.4-2.8,3.2c1.3,0.5,3.2,3.7,1.7,2.8c-1.5-0.9-4-2-4-2L2,14.6V4H18z"
      />
    </g>
  </svg>
);

const placeholderInstrux = (
  <span>Connect to Instagram and set fallback images in the properties panel&nbsp;&#8594;</span>
);

const applyTypeFilters = (images = [], allowedTypes) =>
  images.filter((image) => !!allowedTypes[image.media_type]);

const renderSocialImages = (images, elemHeight, numberOfImages) =>
  images.map(
    (image, i) =>
      i < numberOfImages &&
      image.media_url && (
        <img
          key={`${i}.${image.id}`}
          className="social-image"
          style={{
            objectFit: "cover",
            maxHeight: "100%",
            height: elemHeight,
            width: elemHeight,
          }}
          src={image.media_type !== "VIDEO" ? image.media_url : image.thumbnail_url}
          alt={image.caption}
        />
      )
  );

const SocialGrid = ({ content, elemProps }) => {
  const account = content.get("account", "");
  const { isLoading, data: socialImages } = useQuery({
    queryKey: [`${INSTAGRAM_IMAGES}${account}`],
    queryFn: () =>
      fetch(`${config.instagramMediaUrl}${encodeURIComponent(account)}`).then((response) =>
        response.json()
      ),
    enabled: account !== "",
    refetchOnWindowFocus: "always",
    refetchOnReconnect: false,
    refetchOnMount: true,
    retry: false,
    staleTime: Infinity,
  });

  const rows = elemProps.get("rows");
  const gap = elemProps.get("autoGap", false) && +rows === 1 ? "auto" : elemProps.get("gap");
  const overflow = elemProps.get("overflow", false) && +rows === 1;
  const allowVideo = content.get("allowVideo", 1);
  const allowImage = content.get("allowImage", 1);
  const allowCarousel = content.get("allowCarousel", 1);
  const allowedTypes = {
    VIDEO: allowVideo,
    IMAGE: allowImage,
    CAROUSEL_ALBUM: allowCarousel,
  };

  const elemWidth = elemProps.get("w");
  const alignment = elemProps.get("alignment", "center");
  const elemHeight = elemProps.get("h");
  const calcHeight =
    gap === "auto" ? Math.floor(elemHeight / rows) : elemHeight / rows - gap + gap / rows;
  const numberOfImages =
    gap === "auto"
      ? Math.floor(elemWidth / calcHeight) * rows
      : Math.floor((elemWidth + gap) / (calcHeight + gap)) * rows;
  const filteredSocialImages = applyTypeFilters(socialImages?.data, allowedTypes);
  const designContext = elemProps.get(DESIGN_CONTEXT);
  const { generateDesignAssetUrl } = DesignContextUtils.fromContext(designContext);
  const fallbackElemProps = elemProps.merge({ w: calcHeight, h: calcHeight });
  const fallbackImages = content.get("items")
    ? content
        .get("items", [])
        .map((image, i) => ({
          id: image.key,
          caption: image.desc || image.title,
          title: image.title,
          media_url: generateDesignAssetUrl({
            content: content.get("items").get(i),
            elemProps: fallbackElemProps,
          }),
          media_type: "FALLBACK",
        }))
        .toArray()
    : [];

  const showPlaceholder =
    ((!fallbackImages || !fallbackImages.length) &&
      (!filteredSocialImages || !filteredSocialImages.length)) ||
    isLoading;

  return showPlaceholder ? (
    <div>
      <PlaceholderBackground icon={placeholderIcon} instrux={placeholderInstrux} size="small" />
    </div>
  ) : (
    <div
      className="si-social-grid"
      style={{
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: overflow ? "inline-flex" : "grid",
          height: elemHeight + "px",
          justifyContent: gap === "auto" ? "space-between" : alignment,
          gridTemplateColumns: `repeat(auto-fit, ${calcHeight}px)`,
          gap: gap === "auto" ? "auto" : `${gap}px`,
        }}
      >
        {renderSocialImages(
          filteredSocialImages.length ? filteredSocialImages : fallbackImages,
          calcHeight,
          numberOfImages + 1
        )}
      </div>
    </div>
  );
};

export default SocialGrid;
